import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {map, switchMap} from 'rxjs';
import cytoscape from 'cytoscape';
import {AssetCytoscape} from 'src/app/common/models/ai-reports/shared/assets';
import {zCytoscapeAssetJson} from 'src/app/common/models/ai-reports/shared/cytoscape-schema';
import {ClientAssetService} from 'src/app/services/AIReports/client-asset-service';
import {AiChatFacade} from '@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/chat/ai-chat.facade';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-cytoscape-graph',
  standalone: true,
  imports: [],
  templateUrl: './cytoscape-graph.component.html',
  styleUrl: './cytoscape-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CytoscapeGraphComponent {
  // Default minZoom is unbounded; floor it relative to the auto-fit zoom, mirroring VisjsRenderService.MIN_ZOOM_FACTOR.
  private static readonly MIN_ZOOM_FACTOR = 0.5;

  readonly asset = input.required<AssetCytoscape>();

  readonly cytoscapeContainer = viewChild<ElementRef<HTMLDivElement>>('cytoscapeContainer');

  private readonly clientAssetService = inject(ClientAssetService);
  private readonly facade = inject(AiChatFacade);
  // Snapshot: safe because the component is destroyed when the active thread changes.
  private readonly threadId = this.facade.activeThreadId();
  private cy?: cytoscape.Core;

  readonly dataResource = rxResource({
    params: () => ({storagePath: this.asset().storagePath}),
    stream: ({params}) =>
      this.clientAssetService
        .downloadAssetJson<unknown>(params.storagePath)
        .pipe(map((raw) => zCytoscapeAssetJson.parse(raw))),
  });

  readonly backgroundResource = rxResource({
    params: () => {
      const bg = this.dataResource.value()?.background;
      return bg ? {backgroundAssetId: bg} : undefined;
    },
    stream: ({params}) =>
      this.clientAssetService.getMeta(this.threadId, params.backgroundAssetId).pipe(
        switchMap((bgAsset) => {
          const storagePath = 'storagePath' in bgAsset ? (bgAsset as { storagePath: string }).storagePath : '';
          const storageUrl = new URL(`${environment.apiBaseUrl}${environment.endpoint.assets.base}/storage`);
          storageUrl.searchParams.set('path', storagePath);
          const url = storageUrl.toString();
          return this.clientAssetService.downloadImageAsObjectUrl(url);
        })
      ),
  });

  readonly backgroundUrl = computed(() =>
    this.backgroundResource.hasValue() ? this.backgroundResource.value() : null
  );

  constructor() {
    effect(() => {
      if (!this.dataResource.hasValue()) return;
      const container = this.cytoscapeContainer()?.nativeElement;
      if (!container) return;

      const data = this.dataResource.value()!;
      this.cy?.destroy();
      const cy = cytoscape({
        container,
        elements: data.cytoscape.elements,
        // style: data.cytoscape.style,
        style: [...data.cytoscape.style,
          {
            selector: 'node',
            style: {
              'background-image-crossorigin': 'use-credentials'
            }
          }
        ],
        userZoomingEnabled: true,
        userPanningEnabled: true,
        autoungrabify: true,
        // Forces sync fitting even for layouts that default to animate: true (e.g. cose) — see minZoom below.
        layout: { ...(data.cytoscape.layout ?? { name: 'preset' }), animate: false },
      });
      this.cy = cy;

      // animate: false above means the layout has already fit synchronously, so cy.zoom() here is the final scale.
      cy.minZoom(cy.zoom() * CytoscapeGraphComponent.MIN_ZOOM_FACTOR);
    });

    inject(DestroyRef).onDestroy(() => {
      this.cy?.destroy();
      const url = this.backgroundResource.value();
      if (url) URL.revokeObjectURL(url);
    });
  }
}
